import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { CrudOperation } from '@shared/models';
import { AppNotificationService, ModalService } from '@shared/services';
import { QueryOptions } from 'odata-query';
import { forkJoin } from 'rxjs';
import { GeneralRatesRuleEngineGridComponent } from '../../components/general-rates-rule-engine-grid/general-rates-rule-engine-grid.component';
import {
  Collection,
  GeneralRatesRuleEngine,
  GridItemCreateEvent,
  GridItemUpdateEvent,
} from '../../models';
import { GeneralRatesRuleEngineService } from '../../services';

@Component({
  selector: 'gstar-general-rates-rule-engine-page',
  templateUrl: 'general-rates-rule-engine-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GeneralRatesRuleEnginePageComponent implements OnInit {
  private generalRatesRuleEngineService = inject(GeneralRatesRuleEngineService);
  private appNotificationService = inject(AppNotificationService);
  private translateService = inject(TranslateService);
  private modalService = inject(ModalService);

  public items: WritableSignal<Collection<GeneralRatesRuleEngine>> = signal({
    total: 0,
    items: [],
  });

  public error: WritableSignal<string | null> = signal(null);
  public isLoading: WritableSignal<boolean> = signal(false);
  public selectedItems: WritableSignal<GeneralRatesRuleEngine[]> = signal([]);

  public query: Partial<QueryOptions<GeneralRatesRuleEngine>> = {
    count: true,
    top: 10,
    skip: 0,
  };

  grid = viewChild<GeneralRatesRuleEngineGridComponent>('grid');

  private itemName = this.translateService.instant(
    'admin.dashboard.generalRatesRuleEngineTitle'
  );

  private deleteModalId = 'general-rates-rule-engine-delete-confirm-modal';

  ngOnInit(): void {
    this.fetchItems();
  }

  create(gridItem: GridItemCreateEvent<GeneralRatesRuleEngine>): void {
    let isSuccess = false;

    this.generalRatesRuleEngineService.create(gridItem.item).subscribe({
      next: () => {
        gridItem.onSuccess();
        isSuccess = true;
        this.error.set(null);
        this.fetchItems();

        this.appNotificationService.crudNotify(
          CrudOperation.Create,
          this.itemName,
          isSuccess
        );
      },
      error: err => {
        isSuccess = false;
        const errorMessage = err?.message || 'Error while creating the record';
        this.error.set(errorMessage);
        console.error('Error while creating the record:', err);

        this.appNotificationService.crudNotify(
          CrudOperation.Create,
          this.itemName,
          isSuccess
        );
      },
    });
  }

  update(gridItem: GridItemUpdateEvent<GeneralRatesRuleEngine>): void {
    const selectedItems = this.selectedItems();

    if (selectedItems.length > 1) {
      const newRate = gridItem.item.rate;
      const newRates =
        gridItem.item.rates && gridItem.item.rates.length > 0
          ? [...gridItem.item.rates]
          : undefined;
      const newExplanation = gridItem.item.explanation?.trim() ?? '';

      const itemsToUpdate = selectedItems.map(item => ({
        ...item,
        ...(newRates
          ? { rates: newRates, rate: newRates[0] }
          : newRate !== undefined && newRate !== null
            ? { rate: newRate }
            : {}),
        ...(newExplanation ? { explanation: newExplanation } : {}),
      }));

      forkJoin(
        itemsToUpdate.map(item =>
          this.generalRatesRuleEngineService.update(item)
        )
      ).subscribe({
        next: () => {
          gridItem.onSuccess();
          this.selectedItems.set([]);
          this.grid()?.clearSelection();
          this.error.set(null);
          this.fetchItems();

          this.appNotificationService.crudNotify(
            CrudOperation.Update,
            this.itemName,
            true
          );
        },
        error: err => {
          const errorMessage =
            err?.message || 'Error while bulk updating the records';
          this.error.set(errorMessage);

          this.appNotificationService.crudNotify(
            CrudOperation.Update,
            this.itemName,
            false
          );
        },
      });

      return;
    }

    let isSuccess = false;

    this.generalRatesRuleEngineService.update(gridItem.item).subscribe({
      next: () => {
        gridItem.onSuccess();
        isSuccess = true;
        this.error.set(null);
        this.fetchItems();

        this.appNotificationService.crudNotify(
          CrudOperation.Update,
          this.itemName,
          isSuccess
        );
      },
      error: err => {
        isSuccess = false;
        const errorMessage = err?.message || 'Error while updating the record';
        this.error.set(errorMessage);
        console.error('Error while updating the record:', err);

        this.appNotificationService.crudNotify(
          CrudOperation.Update,
          this.itemName,
          isSuccess
        );
      },
    });
  }

  queryUpdate(query: Partial<QueryOptions<GeneralRatesRuleEngine>>): void {
    this.query = query;
    this.fetchItems();
  }

  private fetchItems(): void {
    this.isLoading.set(true);
    this.generalRatesRuleEngineService
      .get({ ...this.query, count: true })
      .subscribe({
        next: (itemCollection: Collection<GeneralRatesRuleEngine>) => {
          this.items.set(itemCollection);
          this.error.set(null);
          this.isLoading.set(false);
        },
        error: err => {
          const errorMessage =
            err?.message || 'Error while fetching the records';
          this.error.set(errorMessage);
          console.error('Error while fetching the records:', err);
          this.isLoading.set(false);
        },
      });
  }

  onItemsSelected(selection: GeneralRatesRuleEngine[]): void {
    this.selectedItems.set(selection);
  }

  onDeleteRequested(item: GeneralRatesRuleEngine): void {
    const itemsToDelete = this.getDeleteTargets(item);

    this.modalService.open(
      ConfirmationModalComponent,
      { appModalId: this.deleteModalId },
      {
        title: this.translateService.instant('admin.modal.confirmationTitle'),
        content: this.getDeleteConfirmationMessage(itemsToDelete),
        modalId: this.deleteModalId,
        confirmationFn: () => this.confirmDelete(itemsToDelete),
      }
    );
  }

  private confirmDelete(itemsToDelete: GeneralRatesRuleEngine[]): void {
    let isSuccess = false;

    forkJoin(
      itemsToDelete.map(item =>
        this.generalRatesRuleEngineService.delete(item.generalRatesRuleEngineId)
      )
    ).subscribe({
      next: () => {
        isSuccess = true;
        this.selectedItems.set([]);
        this.grid()?.clearSelection();
        this.fetchItems();

        this.appNotificationService.crudNotify(
          CrudOperation.Delete,
          this.itemName,
          isSuccess
        );
      },
      error: err => {
        isSuccess = false;
        const errorMessage = err?.message || 'Error while deleting the record';
        this.error.set(errorMessage);
        console.error('Error while deleting the record:', err);

        this.appNotificationService.crudNotify(
          CrudOperation.Delete,
          this.itemName,
          isSuccess
        );
      },
    });
  }

  private getDeleteTargets(
    item: GeneralRatesRuleEngine
  ): GeneralRatesRuleEngine[] {
    const selectedItems = this.selectedItems();

    if (selectedItems.length > 1) {
      const selectedIds = new Set(
        selectedItems.map(selectedItem => selectedItem.generalRatesRuleEngineId)
      );

      if (selectedIds.has(item.generalRatesRuleEngineId)) {
        return selectedItems;
      }
    }

    return [item];
  }

  private getDeleteConfirmationMessage(
    itemsToDelete: GeneralRatesRuleEngine[]
  ): string {
    if (itemsToDelete.length === 1) {
      return this.translateService.instant(
        'admin.grid.deleteGeneralRatesRuleEngineConfirmMessage'
      );
    }

    return this.translateService.instant(
      'admin.grid.deleteGeneralRatesRuleEngineBulkConfirmMessage',
      { count: itemsToDelete.length }
    );
  }
}
